import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AadhaarVerificationScreen from './AadhaarVerificationScreen';
import PANVerificationScreen from './PANVerificationScreen';
import BankVerificationScreen from './BankVerificationScreen';

const VerificationContainer = () => {
    const [activeStep, setActiveStep] = useState('aadhaar');
    const [searchParams, setSearchParams] = useSearchParams();
    const [verificationStatus, setVerificationStatus] = useState({
        aadhaar: { verified: false, pending: false, photoSubmitted: false },
        pan: { verified: false, pending: false, photoSubmitted: false },
        bank: { verified: false },
        manualKYCStatus: null,
        isFullyVerified: false,
        verificationStep: 'aadhaar'
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Get user info from localStorage
    const tempUserInfo = JSON.parse(localStorage.getItem('tempUserInfo') || '{}');

    // Fetch verification status from backend
    // Update the fetchVerificationStatus function in VerificationContainer.js:

    const fetchVerificationStatus = async () => {
        try {
            if (!tempUserInfo.id || tempUserInfo.id === 'undefined' || !tempUserInfo.id.match(/^[0-9a-fA-F]{24}$/)) {
                setLoading(false);
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/users/get-user-verification/${tempUserInfo.id}`);

            if (response.ok) {
                const data = await response.json();

                if (data.success && data.verification) {
                    const v = data.verification;
                    const normalizedVerification = {
                        aadhaar: {
                            verified: v.aadhaar?.verified || false,
                            pending: v.aadhaar?.pending || false,
                            photoSubmitted: v.aadhaar?.photoSubmitted || !!(v.aadhaar?.photoUrl),
                            verifiedAt: v.aadhaar?.verifiedAt || null
                        },
                        pan: {
                            verified: v.pan?.verified || false,
                            pending: v.pan?.pending || false,
                            photoSubmitted: v.pan?.photoSubmitted || !!(v.pan?.photoUrl),
                            verifiedAt: v.pan?.verifiedAt || null
                        },
                        bank: {
                            verified: v.bank?.verified || false,
                            accountNumber: v.bank?.accountNumber || null,
                            verifiedAt: v.bank?.verifiedAt || null
                        },
                        manualKYCStatus: v.manualKYC?.status || null,
                        isFullyVerified: v.isFullyVerified || false,
                        verificationStep: v.verificationStep || 'aadhaar'
                    };

                    setVerificationStatus(normalizedVerification);
                }
            }
        } catch (error) {
            console.error('Failed to fetch verification status:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tempUserInfo.id) {
            fetchVerificationStatus();
        } else {
            setLoading(false);
        }
    }, [tempUserInfo.id]);

    useEffect(() => {
        // Check URL parameter for active step
        const step = searchParams.get('step');
        if (step && ['aadhaar', 'pan', 'bank'].includes(step)) {
            setActiveStep(step);
        }
    }, [searchParams]);

    // Check if user info exists
    if (!tempUserInfo.id) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-3 sm:p-4">
                <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
                    <div className="text-center">
                        <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-gray-900">
                            Session Expired
                        </h2>
                        <p className="mt-2 text-sm sm:text-base text-gray-600 px-4">
                            Please login again to continue verification
                        </p>
                        <button
                            onClick={() => navigate('/login')}
                            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading verification status...</p>
                </div>
            </div>
        );
    }

    const handleStepClick = (step) => {
        setActiveStep(step);
        setSearchParams({ step });
    };

    // Derived states
    const aadhaarPending = verificationStatus.aadhaar?.pending || verificationStatus.aadhaar?.photoSubmitted;
    const panPending = verificationStatus.pan?.pending || verificationStatus.pan?.photoSubmitted;
    const anyDocSubmitted = aadhaarPending || panPending;
    const isFullyVerified = verificationStatus.isFullyVerified ||
        ((verificationStatus.aadhaar?.verified || verificationStatus.pan?.verified) &&
            verificationStatus.manualKYCStatus === 'approved');

    // Steps — Aadhaar and PAN are the primary steps; Bank is optional
    const steps = [
        {
            id: 'aadhaar',
            name: 'Aadhaar',
            verified: verificationStatus.aadhaar?.verified || false,
            pending: aadhaarPending && !verificationStatus.aadhaar?.verified,
            icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        {
            id: 'pan',
            name: 'PAN',
            verified: verificationStatus.pan?.verified || false,
            pending: panPending && !verificationStatus.pan?.verified,
            icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        {
            id: 'bank',
            name: 'Bank (Optional)',
            verified: verificationStatus.bank?.verified || false,
            pending: false,
            icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
            )
        }
    ];

    const getStepButtonClass = (step) => {
        if (step.verified) return 'bg-green-600 text-white shadow-sm border-green-600';
        if (step.pending) return 'bg-amber-500 text-white shadow-sm border-amber-500';
        if (activeStep === step.id) return 'bg-blue-600 text-white shadow-sm border-blue-600';
        return 'text-gray-600 hover:text-gray-800 hover:bg-white hover:shadow-sm';
    };

    const getStepIcon = (step) => {
        if (step.verified) {
            return (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
            );
        }
        if (step.pending) {
            return (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        }
        return step.icon;
    };

    const renderActiveComponent = () => {
        const props = {
            verificationStatus,
            onStatusUpdate: fetchVerificationStatus
        };

        switch (activeStep) {
            case 'aadhaar':
                return <AadhaarVerificationScreen {...props} />;
            case 'pan':
                return <PANVerificationScreen {...props} />;
            case 'bank':
                return <BankVerificationScreen {...props} />;
            default:
                return <AadhaarVerificationScreen {...props} />;
        }
    };

    // Progress: based on required steps (Aadhaar + PAN only; bank is optional)
    const requiredSteps = steps.filter(s => s.id !== 'bank');
    const completedRequired = requiredSteps.filter(s => s.verified).length;
    const pendingRequired = requiredSteps.filter(s => s.pending).length;
    const progressPercentage = isFullyVerified ? 100 : anyDocSubmitted ? 60 : (completedRequired / requiredSteps.length) * 100;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Navigation Header */}
            <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                        {/* Logo/Title */}
                        <div className="flex items-center space-x-2 sm:space-x-3">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                <svg className="h-4 w-4 sm:h-6 sm:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-base sm:text-lg font-bold text-gray-900">Verification Center</h1>
                                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                                    {isFullyVerified ? 'Identity Verified ✓' : anyDocSubmitted ? 'Document submitted — awaiting admin approval' : 'Submit Aadhaar or PAN to get verified'}
                                </p>
                            </div>
                        </div>

                        {/* User Info */}
                        <div className="text-center sm:text-right">
                            <p className="text-xs sm:text-sm font-medium text-gray-700">
                                Welcome, {tempUserInfo.name || 'User'}!
                            </p>
                            <p className="text-xs text-gray-500 hidden sm:block">
                                {isFullyVerified ? 'Ready to access platform' : anyDocSubmitted ? 'Pending admin review' : 'Submit Aadhaar or PAN photo'}
                            </p>
                        </div>
                    </div>

                    {/* Step Navigation Tabs */}
                    <div className="mt-6 flex gap-1 bg-gray-100 p-1 rounded-lg">
                        {steps.map((step, index) => (
                            <button
                                key={step.id}
                                onClick={() => handleStepClick(step.id)}
                                className={`flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${getStepButtonClass(step)}`}
                            >
                                <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                                    <span className="text-xs sm:text-sm">{getStepIcon(step)}</span>
                                    <span className="text-xs sm:text-sm font-medium">{step.name}</span>
                                    {step.verified && (
                                        <span className="text-xs bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full">✓</span>
                                    )}
                                    {step.pending && !step.verified && (
                                        <span className="text-xs bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full">⏳</span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3 sm:mt-4 bg-gray-200 rounded-full h-1.5 sm:h-2">
                        <div
                            className={`h-1.5 sm:h-2 rounded-full transition-all duration-500 ${
                                isFullyVerified
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                                    : anyDocSubmitted
                                        ? 'bg-gradient-to-r from-amber-400 to-yellow-500'
                                        : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                            }`}
                            style={{ width: `${progressPercentage}%` }}
                        ></div>
                    </div>

                    {/* Status Message */}
                    {isFullyVerified && (
                        <div className="mt-3 sm:mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-green-800 font-medium">
                                    Identity verified! You can now access the platform.
                                </p>
                            </div>
                        </div>
                    )}
                    {!isFullyVerified && anyDocSubmitted && (
                        <div className="mt-3 sm:mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-amber-800 font-medium">
                                    Document submitted. An admin will review and approve your verification shortly.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Active Component */}
            <div className="transition-all duration-300">
                {renderActiveComponent()}
            </div>
        </div>
    );
};

export default VerificationContainer;